import { useState } from "react";
import { DateTime } from "luxon";
import { Line } from "react-chartjs-2";
import type { ChartData, ChartOptions } from "chart.js";
import type { RevenueTrendData } from "@/hooks/useRevenueTrendData";
import type { Granularity } from "@/lib/periods";
import type { FilterState } from "@/types/filters";
import { getCSSVar } from "@/lib/chart";
import { niceYBounds } from "@/lib/chartScale";
import { Card } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

const TOGGLE_ITEM_CLS =
  "h-auto text-[12px] px-3 py-1 border-black/15 bg-white text-gray-600 font-normal hover:bg-gray-50 hover:text-gray-600 transition-colors data-[state=on]:bg-[var(--brand-dark-teal)] data-[state=on]:text-white data-[state=on]:border-[var(--brand-dark-teal)] data-[state=on]:z-10";

const GRAN_LABELS = { day: "Daily", week: "Weekly", month: "Monthly" } as const;

// Total revenue spans millions; per-DVM-day sits in the hundreds/thousands, where
// "$1.1K" loses the precision that makes the series worth reading.
function formatCompact(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function formatExact(value: number): string {
  return `$${Math.round(value).toLocaleString()}`;
}

type RevMode = "total" | "perDvm";

const MODE_META: Record<RevMode, { seriesLabel: string; toggleLabel: string }> = {
  total: { seriesLabel: "Total Medical Net Revenue", toggleLabel: "Total" },
  perDvm: { seriesLabel: "Med. Net Revenue per DVM/day", toggleLabel: "Per DVM" },
};

// Reads clearly against the light teal fill without competing with the data line.
const NVA_COLOR = "#4b5563";

interface RevenueTrendChartProps {
  trend: RevenueTrendData;
  granularity: FilterState["revGranularity"];
  // Groupings the selected period actually spans — a chart with one point
  // is not a trend, so the rest are disabled rather than hidden.
  availableGranularities: Granularity[];
  // True when the period is calendar-based and grouped by week, so the chart
  // is showing fewer days than the KPI cards above it.
  partialWeeksShown: boolean;
  setGranularity: (g: FilterState["revGranularity"]) => void;
}

export function RevenueTrendChart({
  trend,
  granularity,
  availableGranularities,
  partialWeeksShown,
  setGranularity,
}: RevenueTrendChartProps) {
  const [lineColor] = useState(() => getCSSVar("--brand-dark-teal") || "#044f59");
  const [mode, setMode] = useState<RevMode>("perDvm");

  const perDvm = mode === "perDvm";
  const seriesLabel = MODE_META[mode].seriesLabel;
  const fmtAxis = perDvm ? formatExact : formatCompact;

  const values = trend.points.map((p) => Math.round(perDvm ? p.medRevPerDvmDay : p.totalMedRev));
  const benchmarkSeries = (perDvm ? trend.nvaMedRevPerDvmDay : trend.nvaTotalMedRev).map((v) =>
    v === null ? null : Math.round(v)
  );

  const data: ChartData<"line"> = {
    // Day and week labels are two parts ("7/27 Mon", "W31 7/27") — split onto
    // two axis lines, same as the Utilization and Capacity charts.
    labels:
      granularity === "month"
        ? trend.points.map((p) => p.label)
        : trend.points.map((p) => p.label.split(" ")),
    datasets: [
      {
        label: seriesLabel,
        data: values,
        borderColor: lineColor,
        backgroundColor: `${lineColor}15`,
        fill: true,
        tension: 0.35,
        pointRadius: 3,
        pointBackgroundColor: lineColor,
        borderWidth: 2,
      },
      {
        label: "NVA Avg.",
        data: benchmarkSeries,
        borderColor: NVA_COLOR,
        backgroundColor: "transparent",
        fill: false,
        borderDash: [6, 4],
        tension: 0.35,
        pointRadius: 0,
        borderWidth: 1.5,
      },
    ],
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const point = trend.points[items[0]?.dataIndex ?? -1];
            if (!point) return items[0]?.label ?? "";
            return granularity === "day"
              ? DateTime.fromISO(point.bucketKey).toFormat("ccc M/d")
              : point.label;
          },
          label: (ctx) => `${ctx.dataset.label}: ${formatExact(ctx.parsed.y ?? 0)}`,
          // Surfaces the denominator behind the per-DVM figure, so a low point
          // reads as "few doctors" or "low output per doctor" rather than being
          // ambiguous between the two.
          afterBody: (items) => {
            if (!perDvm) return "";
            const point = trend.points[items[0]?.dataIndex ?? -1];
            return point ? `${point.dvmDays.toLocaleString()} DVM-days` : "";
          },
        },
      },
    },
    scales: {
      y: {
        // Bounded rather than left to Chart.js, which fits the axis to the data
        // exactly and so drew the lowest marker inside the axis line.
        ...niceYBounds(values, benchmarkSeries),
        ticks: { font: { size: 11 }, color: "#6b6b68", callback: (v) => fmtAxis(Number(v)) },
        grid: { color: "#e5e4e0" },
      },
      x: {
        ticks: { font: { size: 11 }, color: "#6b6b68", maxRotation: 0, minRotation: 0 },
        grid: { display: false },
      },
    },
  };

  return (
    <Card className="px-5 py-4 gap-0 shadow-none border-black/10">
      {/* Title row: title left, legend box right */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-[16px] font-medium">Medical Net Revenue Trend</div>
        <Card className="flex-row items-center gap-4 px-3 py-2 shadow-none border-black/10 rounded-lg">
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
            <span className="w-3 h-3 rounded-[2px] shrink-0" style={{ background: lineColor }} />
            {seriesLabel}
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
            <svg width="16" height="8" className="shrink-0">
              <line
                x1="0"
                y1="4"
                x2="16"
                y2="4"
                stroke={NVA_COLOR}
                strokeWidth="1.5"
                strokeDasharray="4 3"
              />
            </svg>
            NVA Avg.
          </div>
        </Card>
      </div>

      <div className="text-[12px] text-gray-500 mb-1">
        {GRAN_LABELS[granularity]} — {seriesLabel}
      </div>

      <div className="flex-1 min-h-0 relative" style={{ height: 260 }}>
        <Line data={data} options={options} />
      </div>

      <div className="flex items-center justify-between mt-3">
        <ToggleGroup
          type="single"
          variant="outline"
          value={mode}
          onValueChange={(v) => {
            if (v) setMode(v as RevMode);
          }}
        >
          {(Object.keys(MODE_META) as RevMode[]).map((m) => (
            <ToggleGroupItem key={m} value={m} className={TOGGLE_ITEM_CLS}>
              {MODE_META[m].toggleLabel}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>

        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-500 font-medium">Group by</span>
          <ToggleGroup
            type="single"
            variant="outline"
            value={granularity}
            onValueChange={(v) => {
              if (v) setGranularity(v as FilterState["revGranularity"]);
            }}
          >
            {(["day", "week", "month"] as const).map((g) => (
              <ToggleGroupItem
                key={g}
                value={g}
                disabled={!availableGranularities.includes(g)}
                className={TOGGLE_ITEM_CLS}
              >
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>
      {partialWeeksShown && (
        <p className="text-[11px] text-gray-400 mt-2">
          The first and last points are part-weeks — a calendar period does not start or end on a
          Monday. Their totals are smaller for that reason; the per-DVM rates divide by the days
          actually elapsed, so those stay comparable.
        </p>
      )}
    </Card>
  );
}
